import { z } from "zod"

/**
 * Zod schemas for NVIS Level 5 Vegetation Description JSON.
 * Used to validate user edits and parsed compact-syntax input.
 */

export const NvisStratumCodeSchema = z.enum(["U", "M", "G"])

export const NvisCoverCodeSchema = z.enum([
  "d",
  "c",
  "i",
  "r",
  "bi",
  "bc",
  "unknown",
])

export const NvisHeightClassSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
  z.literal(7),
  z.literal(8),
])

export const NvisSpeciesRefSchema = z.union([
  z.object({
    species_id: z.string().uuid(),
    collection_id: z.undefined().optional(),
  }),
  z.object({
    species_id: z.undefined().optional(),
    collection_id: z.string().uuid(),
  }),
])

export const NvisDominanceMarkerSchema = z
  .enum(["^", "^^", "+/-"])
  .nullable()

export const NvisStratumSpeciesSchema = z.object({
  ref: NvisSpeciesRefSchema,
  dominance: NvisDominanceMarkerSchema,
  rank: z.number().int().min(1).max(3),
  growth_form_code: z.string().optional(),
})

export const NvisStratumGrowthFormSchema = z.object({
  code: z.string().min(1),
  is_characteristic: z.boolean(),
})

export const NvisStratumSchema = z.object({
  code: NvisStratumCodeSchema,
  is_dominant: z.boolean(),
  species: z.array(NvisStratumSpeciesSchema).max(3),
  growth_forms: z.array(NvisStratumGrowthFormSchema).max(3),
  cover_code: NvisCoverCodeSchema,
  height_class: NvisHeightClassSchema,
})

export const NvisDescriptionSchema = z.object({
  version: z.literal(1),
  strata: z.array(NvisStratumSchema).max(3),
  source: z.enum(["auto", "manual"]),
  field_notes: z.string().optional(),
  updated_at: z.string(),
})

export const NvisFieldMappingSchema = z.object({
  density_field: z.string().optional(),
  height_field: z.string().optional(),
  growth_form_field: z.string().optional(),
})

/**
 * Parse a custom field value (which may be a JSON-encoded string) into a
 * validated NvisDescription. Returns null if the value is empty or invalid.
 */
export const parseNvisDescriptionValue = (
  value: unknown,
): z.infer<typeof NvisDescriptionSchema> | null => {
  if (value === null || value === undefined || value === "") return null
  let parsed: unknown = value
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value)
    } catch {
      return null
    }
  }
  const result = NvisDescriptionSchema.safeParse(parsed)
  return result.success ? result.data : null
}
